<!--
 * @Author: Claude
 * @Description: 欺诈通知查询表单组件
 * @Date: 2023-06-20 14:30:00
 * @Business: 支持通知ID、源交易流水号、拒付流水号、退款流水号等精确查询，支持支付方式、欺诈类型、源交易拒付状态、源交易退款类型多选查询，默认查询最近7天的数据
 * @BusinessChange: 新增支付方式与欺诈类型联动逻辑，当选择支付方式时，根据选择的支付方式调用接口获取对应的欺诈类型选项；当支付方式无值时，欺诈类型下拉框选项清空
 * @DataStructureChange: 新增对接口/dict/listByParent的调用，根据支付方式获取对应的欺诈类型选项
 * @Interaction: 用户可通过筛选条件查询欺诈通知数据，支持导出功能
-->
<template>
  <!-- 使用全局搜索组件，传入欺诈通知特定的搜索条件配置 -->
  <search-component class="fraud-notification"
                    ref="search"
                    :search-condition="searchCondition"
                    :search-buttons="[{
                      type: 'download',
                      permissionCode: 'risk:fraud-notification:download',
                      func: onExportClick
                    }]"
                    :showMerchantSearch="true"
                    :isDisplayReset="true"
                    router-name="/risk/fraud-notification"
                    :fuzzySearchPlaceholder="fuzzySearchPlaceholder">
  </search-component>
</template>

<script>
// 导入所需组件和工具
import SearchComponent from '@/components/table/search-component.vue'; // 导入通用搜索组件
import GlobalData from '@/utils/global/index'; // 导入全局数据服务
import { initSelectedData } from '@/utils/tools/constant';
import { formatDateDisplay } from '@/utils/tools/time'; // 导入日期格式化工具

import FraudNotificationAPI from '../api'; // 导入欺诈通知API服务

/**
 * @vue-component SearchForm
 * @description 欺诈通知查询表单组件，配置搜索条件并处理导出功能
 * @description Fraud notification search form component, configuring search conditions and handling export functionality
 */
export default {
  name: 'SearchForm', // 组件名称 / Component name

  components: {
    SearchComponent, // 注册搜索组件 / Register search component
  },

  props: {
    /**
     * @prop {Function} onExportClick
     * @description 导出按钮点击处理函数，由父组件传入
     * @description Export button click handler, passed from parent component
     */
    onExportClick: {
      type: Function, // 函数类型 / Function type
      required: true, // 必须传入 / Required
    },
  },

  /**
   * @method data
   * @description 组件数据对象，定义搜索条件配置和表单状态
   * @description Component data object, defining search condition configuration and form state
   * @returns {Object} 组件响应式数据 / Component reactive data
   */
  data() {
    // 获取当前日期 / Get current date
    const now = new Date();
    // 计算7天前的日期 / Calculate date 7 days ago
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    return {
      fuzzySearchPlaceholder: `${this.$t('message.fraudNotification.notificationId')}; ${this.$t('message.fraudNotification.transactionId')}; ${this.$t('message.fraudNotification.chargebackId')}; ` +
      `${this.$t('message.fraudNotification.refundId')}`,

      // 支付方式选项 / Payment method options
      paymentMethodOptions: [],
      // 欺诈类型选项 / Fraud type options
      fraudTypeOptions: [],
      // 拒付状态选项 / Dispute presence options
      disputePresenceOptions: [],
      // 退款类型选项 / Refund type options
      refundTypeOptions: [],

      /**
       * @property {Array} searchCondition
       * @description 搜索条件配置数组，每个对象定义一个搜索字段
       * @description Search condition configuration array, each object defines one search field
       */
      searchCondition: [
        {
          key: 'serviceId', // 字段名称 / Field name
          name: 'message.fraudNotification.notificationId', // 显示标签名(i18n) / Display label name (i18n)
          type: 'input', // 控件类型为输入框 / Control type is input box
          placeholder: 'message.fraudNotification.notificationId', // 占位文本 / Placeholder text
        },
        {
          key: 'oriTxnId', // 字段名称 / Field name
          name: 'message.fraudNotification.transactionId', // 显示标签名(i18n) / Display label name (i18n)
          type: 'input', // 控件类型为输入框 / Control type is input box
          placeholder: 'message.fraudNotification.transactionId', // 占位文本 / Placeholder text
        },
        {
          key: 'chargebackId', // 字段名称 / Field name
          name: 'message.fraudNotification.chargebackId', // 显示标签名(i18n) / Display label name (i18n)
          type: 'input', // 控件类型为输入框 / Control type is input box
          placeholder: 'message.fraudNotification.chargebackId', // 占位文本 / Placeholder text
        },
        {
          key: 'refundTxnId', // 字段名称 / Field name
          name: 'message.fraudNotification.refundId', // 显示标签名(i18n) / Display label name (i18n)
          type: 'input', // 控件类型为输入框 / Control type is input box
          placeholder: 'message.fraudNotification.refundId', // 占位文本 / Placeholder text
        },
        {
          key: 'cardBrand', // 字段名称 / Field name
          name: 'message.fraudNotification.paymentMethod', // 显示标签名(i18n) / Display label name (i18n)
          type: 'select', // 控件类型为下拉选择器 / Control type is select
          options: [], // 选项数组，将在组件挂载后通过API获取 / Options array, will be obtained through API after component mounting
          multiple: true, // 支持多选 / Support multiple selection
          changeValue: this.onPaymentMethodChange, // 值变化处理函数 / Value change handler
          placeholder: 'message.fraudNotification.paymentMethod', // 占位文本 / Placeholder text
        },
        {
          key: 'acqFraudType', // 字段名称 / Field name
          name: 'message.fraudNotification.fraudType', // 显示标签名(i18n) / Display label name (i18n)
          type: 'select', // 控件类型为下拉选择器 / Control type is select
          options: [], // 选项数组，将在组件挂载后通过API获取 / Options array, will be obtained through API after component mounting
          multiple: true, // 支持多选 / Support multiple selection
          placeholder: 'message.fraudNotification.fraudType', // 占位文本 / Placeholder text
        },
        {
          key: 'chargebackStatus', // 字段名称 / Field name
          name: 'message.fraudNotification.disputePresence', // 显示标签名(i18n) / Display label name (i18n)
          type: 'select', // 控件类型为下拉选择器 / Control type is select
          options: [], // 选项数组，将在组件挂载后通过API获取 / Options array, will be obtained through API after component mounting
          multiple: true, // 支持多选 / Support multiple selection
          defValue: '',
          placeholder: 'message.fraudNotification.disputePresence', // 占位文本 / Placeholder text
        },
        // {
        //   key: 'refundStatus', // 字段名称 / Field name
        //   name: 'message.fraudNotification.refundType', // 显示标签名(i18n) / Display label name (i18n)
        //   type: 'select', // 控件类型为下拉选择器 / Control type is select
        //   options: [], // 选项数组，将在组件挂载后通过API获取 / Options array, will be obtained through API after component mounting
        //   multiple: true, // 支持多选 / Support multiple selection
        // },
        {
          key: ['startCreateTime', 'endCreateTime'], // 日期范围对应的两个字段名 / Two field names for date range
          name: 'message.fraudNotification.received', // 显示标签名(i18n) / Display label name (i18n)
          type: 'date_time', // 控件类型为日期时间选择器 / Control type is date time picker
          // dateType: 'datetime', // 日期时间类型 / Date time type
          // dateFormat: 'yyyy-MM-dd HH:mm:ss', // 显示格式 / Display format
          // dateValueFormat: 'yyyy-MM-dd HH:mm:ss', // 值格式 / Value format
          defValue: [ // 默认值，最近7天 / Default value, last 7 days
            formatDateDisplay(sevenDaysAgo, 'yyyy-MM-dd HH:mm:ss'), // 开始日期 / Start date
            formatDateDisplay(now, 'yyyy-MM-dd HH:mm:ss'), // 结束日期 / End date
          ],
        },
      ],
    };
  },

  /**
   * @lifecycle mounted
   * @description 组件挂载后的生命周期钩子，初始化表单数据
   * @description Lifecycle hook after component is mounted, initializes form data
   */
  mounted() {
    // 加载字典数据 / Load dictionary data
    setTimeout(() => {
      this.loadDictionaries();
    }, 0);
  },

  methods: {
    /**
     * @method loadDictionaries
     * @description 从全局字典服务加载所需的下拉选项数据
     * @description Load required dropdown option data from global dictionary service
     */
    async loadDictionaries() {
      try {
        // 加载支付方式、欺诈类型、拒付状态和退款类型的字典数据
        // Load dictionary data for payment methods, fraud types, dispute presence and refund types
        const dictionaries = await GlobalData.getDictList([
          'CARD_BRAND', // 支付方式 / Payment methods
          'FRAUD_TYPE', // 欺诈类型 / Fraud types //第一次需要请求方便后续直接从缓存取值
          'O_CHARGEBACK_STATUS', // 拒付状态 / Dispute presence
          // 'O_REFUND_STATUS', // 退款类型 / Refund types
        ]);

        // 保存选项数据 / Save option data
        this.paymentMethodOptions = initSelectedData(dictionaries.CARD_BRAND || [], 'description', 'value', false);
        this.disputePresenceOptions = initSelectedData(dictionaries.O_CHARGEBACK_STATUS || [], 'description', 'value', false);
        // this.refundTypeOptions = initSelectedData(dictionaries.O_REFUND_STATUS || []);

        // 获取搜索组件当前支付方式的值 / Get the current payment method value from the search component
        const search = this.$refs.search; // 获取搜索组件引用 / Get search component reference
        if (search) { // 检查搜索组件引用是否存在 / Check if search component reference exists
          search.setSearchSelectOptions('cardBrand', this.paymentMethodOptions);
          search.setSearchSelectOptions('chargebackStatus', this.disputePresenceOptions);
          // search.setSearchSelectOptions('refundStatus', this.refundTypeOptions);
          const cardBrands = search.getInputValue('cardBrand'); // 获取当前支付方式值 / Get current payment method value
          // 如果存在支付方式值，则调用接口获取对应的欺诈类型 / If payment method value exists, call API to get corresponding fraud types
          if (cardBrands && Array.isArray(cardBrands) && cardBrands.length > 0) {
            this.loadFraudTypesByCardBrands(cardBrands); // 调用加载欺诈类型方法 / Call method to load fraud types
          }
        }
      } catch (error) {
        console.error('加载字典数据失败 / Failed to load dictionary data:', error);
      }
    },

    /**
     * @method loadFraudTypesByCardBrands
     * @description 根据支付方式调用接口获取欺诈类型选项
     * @description Call API to get fraud type options based on payment methods
     * @param {Array} cardBrands - 支付方式代码数组 / Array of payment method codes
     */
    async loadFraudTypesByCardBrands(cardBrands) {
      try {
        const dictionaries = await GlobalData.getDictList([
          'CARD_BRAND', // 支付方式 / Payment methods
        ]);
        const newParams = cardBrands
          .map((p) => {
            const cardBrand = dictionaries.CARD_BRAND.find(
              (item) => item.value === p,
            );
            return cardBrand?.id;
          })
          .filter(Boolean);
        // 调用API服务获取欺诈类型数据 / Call API service to get fraud type data
        const response = await FraudNotificationAPI.getFraudTypesByCardBrands({ parent: newParams.join(',') });
        // 判断接口响应是否成功 / Check if API response is successful
        if (response) {
          // 格式化接口返回的数据为选项格式 / Format API returned data to option format
          const fraudTypeOptions = initSelectedData(response || [], 'description', 'value', false);
          // 更新欺诈类型选项 / Update fraud type options
          this.fraudTypeOptions = fraudTypeOptions;
          // 更新搜索表单中的欺诈类型选项 / Update fraud type options in search form
          const search = this.$refs.search; // 获取搜索组件引用 / Get search component reference
          if (search) { // 检查搜索组件引用是否存在 / Check if search component reference exists
            search.setSearchSelectOptions('acqFraudType', fraudTypeOptions); // 设置欺诈类型选项 / Set fraud type options
          }
        }
      } catch (error) {
        console.error('加载欺诈类型数据失败 / Failed to load fraud type data:', error);
        this.$message.error(this.$t('message.common.loadFailed')); // 显示加载失败提示 / Show load failed message
      }
    },

    /**
     * @method onPaymentMethodChange
     * @description 支付方式变更处理函数，调用接口获取对应的欺诈类型选项
     * @description Payment method change handler, calls API to get corresponding fraud type options
     * @param {Array|String} value - 选择的支付方式值 / Selected payment method value
     */
    onPaymentMethodChange(value) {
      // 获取搜索表单引用 / Get search form reference
      const search = this.$refs.search; // 获取搜索组件引用 / Get search component reference
      if (!search) return; // 如果引用不存在则提前返回 / Return early if reference does not exist

      // 清空欺诈类型字段值 / Clear fraud type field value
      search.setInputValue('acqFraudType', null); // 清空欺诈类型输入值 / Clear fraud type input value
      search.setSearchSelectOptions('acqFraudType', []); // 清空欺诈类型选项 / Clear fraud type options

      // 判断支付方式是否有值 / Check if payment method has value
      const hasCardBrands = value && (Array.isArray(value) ? value.length > 0 : !!value); // 检查是否有支付方式值 / Check if payment method value exists

      if (hasCardBrands) { // 如果有支付方式值 / If payment method value exists
        // 将支付方式值转换为数组 / Convert payment method value to array
        const cardBrands = Array.isArray(value) ? value : [value]; // 转换为数组 / Convert to array
        // 调用接口获取对应的欺诈类型 / Call API to get corresponding fraud types
        this.loadFraudTypesByCardBrands(cardBrands); // 调用加载欺诈类型方法 / Call method to load fraud types
      }
    },

    /**
     * @method getSearchRef
     * @description 获取搜索组件引用，供父组件访问
     * @description Get search component reference for parent component access
     * @returns {Object|null} 搜索组件实例引用 / Search component instance reference
     */
    getSearchRef() {
      return this.$refs.search; // 返回搜索组件引用 / Return search component reference
    },
  },
};
</script>

<style lang="scss" >
html .fraud-notification .fuzzy-input-box {
  width: 670px !important;
}

</style>
